import { useState, useEffect } from 'react';
import { Table, Card, ScrollArea, LoadingOverlay, Box } from '@mantine/core';
import { SortableTableHeader, NonSortableColumnHeader } from '../../common/SortableTableHeader';
import { ColumnAveragesRow } from '../../common/ConfigurableTable';
import { CollapsibleGroupHeader } from '../components/CollapsibleGroupHeader';
import { Pagination } from '../../common/Pagination';
import type { SortConfig, ColumnConfig } from '../../../hooks/useColumnConfig';
import type { DataLibraryParticipant, ColumnAverages } from '../../../types';
import { flattenGroups, type GroupNode } from '../../../utils/groupDataByField';
import { renderDataLibraryCell } from './DataLibraryCellRenderers';
import { FLAG_SEVERITY } from '../../../config/severityConfig';
import classes from '../../common/ConfigurableTable/ConfigurableTable.module.css';
import { ResizableHeaderCell } from '../components/ResizableHeaderCell';
import { useDataLibraryColumnWidths } from '../useDataLibraryTableLayout';
import { ColumnWidthCell } from '../components/ColumnWidthCell';
import { DataLibraryEmptyState } from '../components/DataLibraryEmptyState';

interface DataLibraryTableProps {
  data: DataLibraryParticipant[];
  groupedData: GroupNode<DataLibraryParticipant>[] | null;
  columns: ColumnConfig[];
  sortConfig: SortConfig | null;
  onSort: (key: string) => void;
  loading: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  // Pagination (only shown in flat view)
  page: number;
  pageSize: number;
  totalCount: number;
  /** MDF-41: server averages over all matching rows */
  averages: ColumnAverages;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

function getRowBackground(participant: DataLibraryParticipant): string | undefined {
  if (!participant.flag_severity) return undefined;
  return FLAG_SEVERITY[participant.flag_severity]?.row.background;
}

function ParticipantRow({
  widths,
  participant,
  columns,
}: {
  widths: Record<string, number>;
  participant: DataLibraryParticipant;
  columns: ColumnConfig[];
}) {
  return (
    <Table.Tr
      className={classes.tableRow}
      style={{ backgroundColor: getRowBackground(participant) }}
    >
      {columns.map((col, index) => (
        <ColumnWidthCell
          key={col.id}
          className={index === 0 ? classes.stickyColumn : undefined}
          style={{ minWidth: 200 }}
          width={widths[col.id]}
        >
          {renderDataLibraryCell(participant, col.id)}
        </ColumnWidthCell>
      ))}
    </Table.Tr>
  );
}


export function DataLibraryTable({
  data,
  groupedData,
  columns,
  sortConfig,
  onSort,
  loading,
  hasActiveFilters,
  onClearFilters,
  page,
  pageSize,
  totalCount,
  averages,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: DataLibraryTableProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const layout = useDataLibraryColumnWidths();

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Reset collapsed state when group keys change (AC21)
  const groupKeys = groupedData ? groupedData.map(g => g.key).join('\0') : '';
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupKeys]);

  const averagesRow = (
    <ColumnAveragesRow
      columns={columns}
      averages={averages}
      rows={data}
      totalCount={totalCount}
      getValue={(p, id) => (id === 'attendance' ? p.attendance_percentage : p[id as keyof DataLibraryParticipant])}
      firstCellClassName={classes.stickyColumn}
    />
  );

  const hasData = data.length > 0 || (groupedData && groupedData.length > 0);
  const showEmpty = groupedData ? groupedData.length === 0 : !loading && data.length === 0;

  const tableHeader = (
    <Table.Thead className={classes.tableHeader}>
      <Table.Tr>
        {columns.map((col, index) =>
          col.sortable ? (
            <ResizableHeaderCell
              key={col.id}
              column={col}
              sticky={index === 0}
              style={{ minWidth: 200 }}
              layout={layout}
            >
              <SortableTableHeader
                label={col.label}
                sortKey={col.id}
                currentSort={sortConfig}
                onSort={onSort}
              />
            </ResizableHeaderCell>
          ) : (
            <ResizableHeaderCell
              key={col.id}
              column={col}
              sticky={index === 0}
              style={{ minWidth: 200 }}
              layout={layout}
            >
              <NonSortableColumnHeader label={col.label} />
            </ResizableHeaderCell>
          ),
        )}
      </Table.Tr>
    </Table.Thead>
  );

  // Grouped view — collapsible sections
  if (groupedData) {
    return (
      <Card
        withBorder
        p={0}
        radius={8}
        bg="white"
        style={{ boxShadow: 'none', position: 'relative' }}
        className={classes.tableCard}
      >
        <LoadingOverlay visible={loading && !!hasData} overlayProps={{ blur: 1 }} />
        <ScrollArea scrollbarSize={8} type="auto" offsetScrollbars={false}>
          <Table highlightOnHover className={classes.table}>
            {tableHeader}
            <Table.Tbody>
              {averagesRow}
              {flattenGroups(groupedData, collapsedGroups).map(item =>
                'group' in item ? (
                  <CollapsibleGroupHeader
                    key={`group-${item.group.key}`}
                    label={item.group.label}
                    count={item.group.rows.length}
                    depth={item.group.depth}
                    colSpan={columns.length}
                    isCollapsed={collapsedGroups.has(item.group.key)}
                    onToggle={() => toggleGroup(item.group.key)}
                  />
                ) : (
                  <ParticipantRow key={item.row.id} participant={item.row} columns={columns} widths={layout.columnWidths} />
                ),
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        {showEmpty && <DataLibraryEmptyState message="No participants found" hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />}
      </Card>
    );
  }

  // Flat view
  return (
    <Card
      withBorder
      p={0}
      radius={8}
      bg="white"
      style={{ boxShadow: 'none', position: 'relative' }}
      className={classes.tableCard}
    >
      <LoadingOverlay visible={loading && !!hasData} overlayProps={{ blur: 1 }} />
      <ScrollArea scrollbarSize={8} type="auto" offsetScrollbars={false}>
        <Table highlightOnHover className={classes.table}>
          {tableHeader}
          <Table.Tbody>
            {averagesRow}
            {data.map(p => (
              <ParticipantRow key={p.id} participant={p} columns={columns} widths={layout.columnWidths} />
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      {showEmpty && <DataLibraryEmptyState message="No participants found" hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />}
      {totalCount > 0 && !loading && (
        <Box className={classes.paginationWrapper}>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={totalCount}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[25, 50, 100, 250]}
            entityLabel="participants"
          />
        </Box>
      )}
    </Card>
  );
}
